import React from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import Avatar from '@material-ui/core/Avatar';
import Box from '@material-ui/core/Box';
import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';
import ItemAdjustment from '../ItemsList/ItemAdjustment';
import formatCurrency from '@afosto/utils/formatCurrency';
import resizeImageUrl from '@afosto/utils/resizeImageUrl';
import styled from 'styled-components';
import translations from './translations';

const InvoiceItemContainer = styled.div`
  && {
    display: flex;
    align-items: center;
  }
`;

const InvoiceItem = ({
  item,
  onClick,
  SelectionComponent,
  allowNegativeQuantity,
  isVatShifted,
  isIncludingVat,
}) => {
  const intl = useIntl();
  const hasDescription = item.description && item.description !== '';
  const handleOnClick = () => {
    if (onClick) {
      onClick(item);
    }
  };

  const formattedQuantity = !allowNegativeQuantity ? Math.abs(item.quantity) : item.quantity;

  return (
    <InvoiceItemContainer key={item.id}>
      {SelectionComponent && <SelectionComponent />}
      <ListItem onClick={handleOnClick} button={!!onClick}>
        <ListItemAvatar>
          <Avatar alt={item.description} src={resizeImageUrl(item?.image, 50)} />
        </ListItemAvatar>
        <ListItemText
          primary={hasDescription ? item.description : item.sku}
          secondaryTypographyProps={{
            component: 'div',
          }}
          secondary={
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                {hasDescription && <Typography variant="body2">{item.sku}</Typography>}
                {item.amount !== item.total && item.quantity === 1 && (
                  <Typography variant="body2">
                    {formatCurrency(item.amount / 100, item.currency)}
                  </Typography>
                )}
              </div>
              {(item.quantity > 1 || item.quantity < 0) && (
                <Typography variant="body2">
                  {formattedQuantity}x {formatCurrency(item.amount / 100, item.currency)} ={' '}
                  {formatCurrency(item.subtotal / 100, item.currency)} (
                  {isIncludingVat.is_including_vat
                    ? intl.formatMessage(translations.totals.incl)
                    : intl.formatMessage(translations.totals.excl)}
                  )
                </Typography>
              )}

              <Typography variant="body2">
                {intl.formatMessage(translations.totals.vat)}{' '}
                {isVatShifted && intl.formatMessage(translations.totals.vatShifted)}{' '}
                {item?.vat_percentage}%
              </Typography>

              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2">
                  {intl.formatMessage(translations.totals.subtotal)} (
                  {isIncludingVat
                    ? intl.formatMessage(translations.totals.incl)
                    : intl.formatMessage(translations.totals.excl)}
                  ){' '}
                </Typography>
                {formatCurrency(item.subtotal / 100)}
              </Box>
              {(item?.adjustments ?? []).map(adjustment => (
                <ItemAdjustment adjustment={adjustment} />
              ))}
              <Typography variant="subtitle2" color="textPrimary">
                {intl.formatMessage(translations.totals.total)} (
                {isIncludingVat
                  ? intl.formatMessage(translations.totals.incl)
                  : intl.formatMessage(translations.totals.excl)}
                )
              </Typography>
            </>
          }
        />
        <Typography
          style={{ alignSelf: 'flex-end', marginBottom: 5, marginLeft: 16 }}
          variant="subtitle2"
        >
          {formatCurrency(item.total / 100, item.currency)}
        </Typography>
      </ListItem>
    </InvoiceItemContainer>
  );
};

InvoiceItem.propTypes = {
  allowNegativeQuantity: PropTypes.bool,
  item: PropTypes.object.isRequired,
  isIncludingVat: PropTypes.bool,
  isVatShifted: PropTypes.bool,
  onClick: PropTypes.func,
  SelectionComponent: PropTypes.oneOfType([PropTypes.node, PropTypes.func, PropTypes.string]),
};

InvoiceItem.defaultProps = {
  allowNegativeQuantity: true,
  isVatShifted: false,
  isIncludingVat: true,
  onClick: undefined,
  SelectionComponent: undefined,
};

export default InvoiceItem;
